import Grid from "../shared/Grid/Grid";
import { GridColumnDefs } from "../../models/Grid/gridColumnDefs";
import { useState, useMemo, useEffect } from "react";
import { Utility } from "../../utils/Utility";
import { ReactComponent as DoneIcon } from "../../assets/svg/Done.svg";
import { ReactComponent as InfoIcon } from "../../assets/svg/info_lg.svg";
import { reportPage_LABELS } from "../../constants/labels/ReportingPage";
import Download from "../shared/SVG/Download";
import { Button } from "@mui/material";
import DaterangePicker from "../shared/DaterangePicker/DaterangePicker";
import { userManagement_LABELS } from "../../constants/labels/UserManagement";
import ConfirmationDialog from "../shared/ConfirmationDialog/ConfirmationDialog";
import * as dateFns from "date-fns";
import { ReactComponent as ExportIcon } from "../../assets/svg/Export.svg";
import "../ReportingGrid/ReportingGrid.scss";
import {
  useLazyGetDraftCareServiceInfoQuery,
  useLazyGenerateDraftCareServiceReportQuery,
  useLazyDownloadDraftCareServiceReportQuery,
} from "../../services/rtkQueryServices/reportingService";
import { getPatientBillingReportInfo } from "../../models/request/getPatientBillingReportInfoRequest";


function DraftReportingGrid(props: any){
  const [rowData, setRowData] = useState<any[]>([]);
  const [rowcount, setRowCount] = useState<number>(0);
  const [showExportPopup, setShowExportPopup] = useState<boolean>(false);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const [showDownloadErrorPopup, setShowDownloadErrorPopup] =
    useState<boolean>(false);
  const [dateRange, setDateRange] = useState<any>([]);
  const [error, setError] = useState<string>("");
  const applicationUserId = props?.userId;
  const [filterCriteria, setFilterCriteria] =
    useState<getPatientBillingReportInfo>({
      applicationUserId: props?.userId,
      pageNumber: 1,
      pageSize: 10,
      sortByColumnName: "createdDate",
      sortOrder: "desc",
      filters: [],
      timezoneOffSet: 0,
      dateRangeCondition: dateRange?.length
        ? {
            fromDate: dateRange[0]?.toLocaleDateString("fr-CA"),
            toDate: dateRange[1]?.toLocaleDateString("fr-CA"),
          }
        : null,
    });
  const [isApplyFiltersEnabled, setIsApplyFiltersEnabled] =
    useState<boolean>(false);
  const [resetPagination, setResetPagination] = useState<boolean>(false);
  const [getDraftCareServiceReportInfo] =
    useLazyGetDraftCareServiceInfoQuery();
  const [downloadExcel] = useLazyDownloadDraftCareServiceReportQuery();
  const [generateReport] = useLazyGenerateDraftCareServiceReportQuery();

  const getDraftCareServiceReportInformation = () => {
    getDraftCareServiceReportInfo(filterCriteria).then((res: any) => {
      if (res.isSuccess && res.data?.data) {
        setRowData(res?.data?.data?.listOfPatientBillingReports);
        setRowCount(res?.data?.data?.totalCount);
      }
    });
  };

  const isBpSiteIdVerified = useMemo(() => {
    return props?.isBpSiteIdVerified;
  }, [props?.isBpSiteIdVerified]);
  const isSubscriptionActive = useMemo(() => {
    return props?.isSubscriptionActive;
  }, [props?.isSubscriptionActive]);

  useEffect(() => {
    if (isSubscriptionActive === true && isBpSiteIdVerified === true) {
      getDraftCareServiceReportInformation();
    }
  }, [filterCriteria, isSubscriptionActive, isBpSiteIdVerified]);

  const ReportRequestId = (props: any) => {
    return (
      <div title={props?.data?.requestId}>
        {props?.data?.requestId.slice(0, 5)}
      </div>
    );
  };

  const fromToDate = (props: any) => {
    var fromDate = Utility.ConvertToDate(props?.data?.fromDate);
    var toDate = Utility.ConvertToDate(props?.data?.toDate);
    return `${fromDate} - ${toDate}`;
  };

  const RequestedDate = (props: any) => {
    const onlyDate = Utility.ConvertToDate(props?.data?.requestedDate);
    return onlyDate;
  };

  const RequestedBy = (props: any) => {
    if (props?.data?.hasRequestedBySuperUser) {
      return `${props?.data?.requestedBy} (Inca Support)`;
    } else {
      return `${props?.data?.requestedBy}`;
    }
  };

  const Actions = (props: any) => {
    var downloadExcelRequest: any = {
      applicationUserId: applicationUserId,
      requestId: props?.data?.requestId,
    };

    const getDownloadText = (status: string) => {
      switch (status) {
        case reportPage_LABELS.Intiated:
          return "Request is processing.";
        case reportPage_LABELS.InProgress:
          return "File generation is in progress.";
        case reportPage_LABELS.Generated:
          return "Download File now";
        case reportPage_LABELS.Failed:
          return "File generation failed. Try again.";
        case reportPage_LABELS.Expired:
          return "File not available to download.";
        case reportPage_LABELS.NoData:
          return "Records not available in the selected date range";
        default:
          return "";
      }
    };

    const handleDownload = () => {
      downloadExcel(downloadExcelRequest).then((res: any) => {
        if (res?.isSuccess && res?.data?.data) {
          const blob = new Blob([res?.data?.data], {
            type:
              res?.data?.headers?.get("Content-Type") ||
              "application/octet-stream",
          });

          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download =
            Utility.getFileNameFromResponseHeaders(res?.data?.headers) ||
            "Report.xlsx";
          link.click();
          setTimeout(() => {
            URL.revokeObjectURL(link.href);
          }, 100);
        } else if (res?.isError) {
          setShowDownloadErrorPopup(true);
        }
      });
    };

    return (
      <div title={getDownloadText(props?.data.documentStatus)}>
        {props?.data?.documentStatus === reportPage_LABELS.Generated ? (
          <div>
            <Button className="download" onClick={handleDownload}>
              <Download />
              Download
            </Button>
          </div>
        ) : props?.data?.documentStatus === reportPage_LABELS.Expired ? (
          <div>
            <div className="red">Expired</div>
          </div>
        ) : (
          <div>
            {props?.data?.documentStatus === reportPage_LABELS.NoData
              ? "No Data Found"
              : props?.data?.documentStatus === reportPage_LABELS.InProgress
              ? <span className="yellow">In Progress</span>
              : props?.data?.documentStatus}
          </div>
        )}
      </div>
    );
  };

  const columnDefs: GridColumnDefs[] = [
    {
      headerName: userManagement_LABELS.requestId,
      field: "requestId",
      cellRenderer: ReportRequestId,
    },
    {
      headerName: userManagement_LABELS.fromToDate,
      field: "fromDate",
      cellRenderer: fromToDate,
    },
    {
      headerName: userManagement_LABELS.requestedBy,
      field: "requestedBy",
      cellRenderer: RequestedBy,
    },
    {
      headerName: userManagement_LABELS.requestedDate,
      field: "requestedDate",
      cellRenderer: RequestedDate,
    },
    {
      headerName: userManagement_LABELS.actionStatus,
      field: "actions",
      cellRenderer: Actions,
      align: "center",
    },
  ];
  const onPageChange = (value: number) => {
    setFilterCriteria({
      ...filterCriteria,
      pageNumber: value,
    });
  };
  const onPageSizeChange = (value: number) => {
    setFilterCriteria({
      ...filterCriteria,
      pageNumber: 1,
      pageSize: value,
    });
  };

  const handleExport = () => {
    setIsApplyFiltersEnabled(false);
    setResetPagination(true);
    setDateRange(null);
    generateReport({
      applicationUserId: applicationUserId,
      fromDate: dateRange?.[0]?.toLocaleDateString("fr-CA"),
      toDate: dateRange?.[1]?.toLocaleDateString("fr-CA"),
      timezoneOffSet: dateRange?.length
         ? -new Date().getTimezoneOffset()
         : 0,
      }).then((res) => {
          if (res?.isSuccess && res?.data?.data) {
            setShowExportPopup(true);
          }
          else if(res.isError) {
            setShowErrorPopup(true);
          }
        })
        .catch(() => {
          setShowErrorPopup(true);
        });
  };

  const handleDateRangeChange = (val: any) => {
    if (val !== null) {
      var minStartDate = new Date(
        dateFns
        .startOfDay(dateFns.subMonths(new Date(), 13))
        .setDate(1)
        );
        var maxEndDate = dateFns.endOfDay(new Date());
        var startDate = new Date(val[0]);
        var endDate = new Date(val[1]);
        if (startDate < minStartDate || endDate > maxEndDate) {
          setError("Select date range within past 13 months");
          setIsApplyFiltersEnabled(false);
        } 
        else {
          setError("");
          setIsApplyFiltersEnabled(true);
        }
    } 
    else {
      setError("");
      setIsApplyFiltersEnabled(false);
    }
    setDateRange(val);
  };
  const allowedDateRange = {
      fromDate: dateFns
        .startOfDay(dateFns.subMonths(new Date(), 13))
        .setDate(1),
      toDate: new Date().toLocaleDateString("fr-CA"),
  };
  return (
    <>
      <div className="clinic-management-wrapper clinic-grid-wrapper overflow-scroll d-flex flex-column">
        <Grid
          columnDefs={columnDefs}
          uniqueIdentifierFieldName="id"
          resetPagination={resetPagination}
          rowData={rowData}
          serverSidePagination
          rowCount={rowcount}
          serverSideSorting
          showPagination
          showFilterIcon
          pageSizeList={[10, 20, 50]}
          pageSize={10}
          masterDetail
          showTotalRowCount
          isReporting={true}
          isHeaderDisabled={
            (isSubscriptionActive !== null && !isSubscriptionActive) ||
            (isBpSiteIdVerified !== null && !isBpSiteIdVerified)
          }
          filterTemplate={[
            <>
              <div>
                <DaterangePicker
                  dateRange={dateRange}
                  onChange={handleDateRangeChange}
                  allowedDateRange={allowedDateRange}
                />
                {error && (
                  <div style={{ paddingLeft: "5px" }} className="error-message">
                    {error}
                  </div>
                )}
              </div>
              <div className="pt-2 px-2">
                <Button
                  className="export"
                  disabled={!isApplyFiltersEnabled}
                  onClick = {handleExport}
                  >
                  <ExportIcon />
                  <span>Export</span>
                </Button>
              </div>
            </>,
          ]}
          resetSuccess={() => {
            setResetPagination(false);
          }}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
        {((isSubscriptionActive !== null && !isSubscriptionActive) ||
          (isBpSiteIdVerified !== null && !isBpSiteIdVerified)) && (
          <div className="grid-overlay">
            <div className="overlay-text">
              {isSubscriptionActive !== null && !isSubscriptionActive
                ? "Renew your subscription to generate reports."
                : isBpSiteIdVerified !== null &&
                  !isBpSiteIdVerified &&
                  "Verify BP Site ID to generate reports."}
            </div>
          </div>
        )}
      </div>

      <ConfirmationDialog
        open={showExportPopup}
        contentBody={
          <div className="d-flex flex-column justify-content-center  align-items-center gap-8">
            <DoneIcon />
            <div className="confirmation-header">Request Successful</div>
            <p className="pt-4 dialog-body">
              Your request has been received successfully. We will notify you
              over email once the file is ready. Please check your email.
            </p>
          </div>
        }
        onOk={() => {
          setShowExportPopup(false);
          getDraftCareServiceReportInformation();
          props?.setTabValue(2);
        }}
        onClose={() => {
          setShowExportPopup(false);
          getDraftCareServiceReportInformation();
          props?.setTabValue(2);
        }}
        okButtonName="OK"
        okButtonClassName="success-ok"
      />
      <ConfirmationDialog
        open={showErrorPopup}
        contentBody={
          <div className="d-flex flex-column justify-content-center  align-items-center gap-8">
            <InfoIcon />
            <div className="error-confirmation-header">Error</div>
            <p className="pt-4 dialog-body">
              We are unable to process your request at this moment, please try
              again later.
            </p>
          </div>
        }
        onOk={() => {
          setShowErrorPopup(false);
        }}
        onClose={() => {
          setShowErrorPopup(false);
        }}
        okButtonName="OK"
        okButtonClassName="success-ok"
      />
      <ConfirmationDialog
        open={showDownloadErrorPopup}
        contentBody={
          <div className="d-flex flex-column justify-content-center  align-items-center gap-8">
            <InfoIcon />
            <div className="error-confirmation-header">Error</div>
            <p className="pt-4 dialog-body">File is not available.</p>
          </div>
        }
        onOk={() => {
          setShowDownloadErrorPopup(false);
          getDraftCareServiceReportInformation();
        }}
        onClose={() => {
          setShowDownloadErrorPopup(false);
          getDraftCareServiceReportInformation();
        }}
        okButtonName="OK"
        okButtonClassName="success-ok"
      />
    </>
  );
}

export default DraftReportingGrid;